// ============================================
// MEDFLOW - Auth Store (Zustand)
// ============================================

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

type AuthProvider = 'legacy' | 'supabase';

interface User {
  id: string;
  email: string;
  name: string;
  phone?: string;
  role?: string;
  defaultClinicId?: string | null;
}

interface Clinic {
  id: string;
  name: string;
  slug: string;
  timezone?: string;
  phone?: string | null;
  email?: string | null;
  isActive?: boolean;
}

interface Membership {
  id: string;
  clinicId: string;
  role: string;
  status: string;
  permissions?: Record<string, unknown>;
  clinic: Clinic;
}

interface AuthState {
  user: User | null;
  clinic: Clinic | null;
  memberships: Membership[];
  token: string | null;
  provider: AuthProvider;
  isAuthenticated: boolean;
  isHydrated: boolean;
  
  // Actions
  login: (user: User, clinic: Clinic, token: string) => void;
  setSupabaseAuth: (payload: { user: User; clinic: Clinic | null; memberships: Membership[] }, token: string | null) => void;
  logout: () => void;
  updateUser: (user: Partial<User>) => void;
  setHydrated: (isHydrated: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      clinic: null,
      memberships: [],
      token: null,
      provider: 'legacy',
      isAuthenticated: false,
      isHydrated: true,
      
      login: (user, clinic, token) => {
        set({
          user,
          clinic,
          memberships: [],
          token,
          provider: 'legacy',
          isAuthenticated: true,
          isHydrated: true,
        });
      },

      setSupabaseAuth: (payload, token) => {
        set({
          user: payload.user,
          clinic: payload.clinic,
          memberships: payload.memberships,
          token,
          provider: 'supabase',
          isAuthenticated: true,
          isHydrated: true,
        });
      },
      
      logout: () => {
        set({
          user: null,
          clinic: null,
          memberships: [],
          token: null,
          provider: 'legacy',
          isAuthenticated: false,
          isHydrated: true,
        });
      },
      
      updateUser: (userData) => {
        set((state) => ({
          user: state.user ? { ...state.user, ...userData } : null,
        }));
      },

      setHydrated: (isHydrated) => {
        set({ isHydrated });
      },
    }),
    {
      name: 'medflow-auth',
      partialize: (state) => ({
        user: state.user,
        clinic: state.clinic,
        memberships: state.memberships,
        token: state.token,
        provider: state.provider,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
